// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once admin JS imports sp apps directly.
/** Classic-script global required because MiniRacer loads it before browser modules. */
globalThis.AdminSelectChoices = (() => {
  /** Returns a matching choice value or the first enabled selectable value; `undefined` when neither exists. */
  function selectValue(items, selected, { branchesSelectable = true, navigation } = {}) {
    const choices = items.flatMap((item) => selectableItems(item, { branchesSelectable, navigation }));
    const matching = choices.find((item) => String(item.value) === String(selected));
    const fallback = choices.find((item) => !isDisabled(item));

    return matching?.value ?? fallback?.value;
  }

  /** Returns the values of the rows the navigation renders as selectable, as strings. */
  function selectableValues(items, { branchesSelectable = true, navigation } = {}) {
    return items
      .flatMap((item) => selectableItems(item, { branchesSelectable, navigation }))
      .map((item) => String(item.value));
  }

  /** Returns the selected choice values once each, in choice order. */
  function selectValues(items, selected, { branchesSelectable = true, navigation } = {}) {
    const pending = new Set(selectionValues(selected).map(String));

    return items
      .flatMap((item) => selectableItems(item, { branchesSelectable, navigation }))
      .filter((item) => pending.delete(String(item.value)))
      .map((item) => item.value);
  }

  /** Returns `selected` as an array, decoding a JSON array string. */
  function selectionValues(selected) {
    if (Array.isArray(selected)) {
      return selected;
    }

    if (typeof selected !== "string") {
      return selected == null ? [] : [selected];
    }

    try {
      const values = JSON.parse(selected);

      return Array.isArray(values) ? values : [selected];
    } catch {
      return [selected];
    }
  }

  // sp-select reads a disabled flag as on only for `true` or `"true"`.
  function isDisabled(item) {
    return [item.disabled, item.props?.disabled].some((flag) => flag === true || flag === "true");
  }

  // Follows sp-select: a primitive item is its own value and headers are skipped. Navigation branches follow
  // branchesSelectable; flat rows and accordion children keep props.selectable. Flat navigation renders the top level
  // only. The other navigations open only `hasChildren` branches: accordion any whose `header` is empty, drilldown
  // only those without a `header` key, which `sp-list` lists as a subheader. Accordion navigation is one level deep: its children
  // are rows whatever they hold, and their own children are not rendered.
  function selectableItems(item, { branchesSelectable = true, navigation, depth = 0 } = {}) {
    if (item === null || typeof item !== "object") {
      return [{ title: String(item), value: item }];
    }

    const accordion = navigation === "accordion";
    const flat = !navigation || navigation === "flat";

    if ((flat && depth > 0) || (accordion && depth > 1)) {
      return [];
    }

    const row = !("header" in item) && item.value !== undefined;

    if (accordion && depth === 1) {
      return row && item.props?.selectable !== false ? [item] : [];
    }

    const children =
      item.hasChildren && (accordion ? !item.header : !("header" in item))
        ? (item.children || []).flatMap((child) =>
            selectableItems(child, { branchesSelectable, navigation, depth: depth + 1 }),
          )
        : [];
    const selectable = item.hasChildren && !flat ? branchesSelectable : item.props?.selectable !== false;

    return row && selectable ? [item, ...children] : children;
  }

  /** Returns a fresh empty placeholder item. */
  function blankSelectItem() {
    return {
      title: "",
      value: "",
      placeholder: true,
      dataset: {},
    };
  }

  return {
    selectValue,
    selectValues,
    selectableValues,
    selectionValues,
    blankSelectItem,
  };
})();
